import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Grid, Card, CardContent, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText } from '@mui/material';

interface Tool {
    name: string;
    type: string;
    description: string;
}

interface Agent {
    _id: string;
    name: string;
    role: string;
    description: string;
    prompt_template: string;
    tools: Tool[];
    created_at: string;
    updated_at: string;
}

interface Actor {
    _id: string;
    name: string;
    description: string;
    skills: string;
    agents: string[]; // IDs de los agentes
}

export const ActorDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [actor, setActor] = useState<Actor | null>(null);
    const [agentDetails, setAgentDetails] = useState<{ [key: string]: Agent }>({});
    const [open, setOpen] = useState(false);
    const [availableAgents, setAvailableAgents] = useState<Agent[]>([]);
    const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);

    useEffect(() => {
        const fetchActor = async () => {
            try {
                const response = await fetch(`http://localhost:5000/api/actors/${id}`);
                const data = await response.json();
                setActor(data);
                fetchAgentDetails(data.agents);
            } catch (error) {
                console.error('Error al obtener los detalles del actor:', error);
            }
        };

        const fetchAvailableAgents = async () => {
            try {
                const response = await fetch('http://localhost:5000/api/agents');
                const data = await response.json();
                setAvailableAgents(data);
            } catch (error) {
                console.error('Error al obtener los agentes disponibles:', error);
            }
        };

        fetchActor();
        fetchAvailableAgents();
    }, [id]);

    const fetchAgentDetails = async (agentIds: string[]) => {
        const details: { [key: string]: Agent } = {};
        for (const agentId of agentIds) {
            try {
                const response = await fetch(`http://localhost:5000/api/agents/${agentId}`);
                const data = await response.json();
                details[agentId] = data;
            } catch (error) {
                console.error(`Error al obtener los detalles del agente ${agentId}:`, error);
            }
        }
        setAgentDetails(details);
    };

    const handleAddAgent = async () => {
        if (!selectedAgentId) return;

        try {
            const response = await fetch(`http://localhost:5000/api/actors/${id}/addAgent`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ agentId: selectedAgentId }),
            });

            if (!response.ok) {
                throw new Error('Error al agregar el agente al actor');
            }

            const updatedActor = await response.json();
            setActor(updatedActor);
            fetchAgentDetails([...updatedActor.agents]);
            setOpen(false);
            setSelectedAgentId(null);

        } catch (error) {
            console.error('Error al agregar el agente al actor:', error);
        }
    };

    if (!actor) {
        return <Typography>Cargando...</Typography>;
    }

    return (
        <Container maxWidth="md">
            <Typography variant="h4" component="h1" gutterBottom>
                {actor.name}
            </Typography>
            <Typography variant="h6" component="h2">
                ID: {actor._id}
            </Typography>
            <Typography variant="body1" paragraph>
                {actor.description}
            </Typography>
            <Typography variant="body1" paragraph>
                Conocimientos: {actor.skills}
            </Typography>

            <Button variant="contained" onClick={() => setOpen(true)} sx={{ mt: 2, mb: 4 }}>
                Agregar Agente
            </Button>

            <Typography variant="h5" component="h2" gutterBottom>
                Agentes Asociados
            </Typography>

            <Grid container spacing={2}>
                {actor.agents.map((agentId) => {
                    const agent = agentDetails[agentId];
                    return (
                        <Grid item key={agentId} xs={12} sm={6} md={4}>
                            <Card sx={{ height: '100%' }}>
                                <CardContent>
                                    <Typography variant="h6" component="div">
                                        {agent ? agent.name : 'Cargando...'}
                                    </Typography>
                                    {agent && (
                                        <>
                                            <Typography variant="body2" color="text.secondary">
                                                Rol: {agent.role}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Descripción: {agent.description.substring(0, 100)}...
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Herramientas: {agent.tools.length}
                                            </Typography>
                                        </>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Modal para seleccionar agente */}
            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogTitle>Seleccionar Agente</DialogTitle>
                <DialogContent>
                    <List>
                        {availableAgents.map((agent) => (
                            <ListItem 
                                button 
                                key={agent._id} 
                                onClick={() => setSelectedAgentId(agent._id)}
                                selected={selectedAgentId === agent._id}
                            >
                                <ListItemText 
                                    primary={agent.name} 
                                    secondary={`Rol: ${agent.role}`} 
                                />
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancelar</Button>
                    <Button 
                        variant="contained" 
                        onClick={handleAddAgent}
                        disabled={!selectedAgentId}
                    >
                        Agregar
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};